import Clarity from '@microsoft/clarity';

const CLARITY_PROJECT_ID = 'wz5l7d6exs';
const SESSION_STORAGE_KEY = 'genseo:clarity-session-id';

let initialized = false;

type ClarityTagValue = string | string[] | number | boolean | null | undefined;
export type ClarityTags = Record<string, ClarityTagValue>;

const sanitizeTagValue = (value: ClarityTagValue): string | string[] | null => {
  if (value === null || typeof value === 'undefined') return null;
  if (Array.isArray(value)) {
    const sanitized = value
      .map((item) => String(item).trim())
      .filter(Boolean);
    return sanitized.length > 0 ? sanitized : null;
  }
  const sanitized = String(value).trim();
  return sanitized ? sanitized : null;
};

export const normalizeShopDomain = (rawShop: string) => {
  const cleaned = rawShop.trim().toLowerCase().replace(/^https?:\/\//, '');
  const withoutQuery = cleaned.split('?')[0];
  const adminStoreMatch = withoutQuery.match(/admin\.shopify\.com\/store\/([^/]+)/);
  if (adminStoreMatch?.[1]) {
    return `${adminStoreMatch[1]}.myshopify.com`;
  }
  const hostOnly = withoutQuery.split('/')[0];
  if (hostOnly.includes('.myshopify.com')) {
    return hostOnly;
  }
  if (!hostOnly.includes('.')) {
    return `${hostOnly}.myshopify.com`;
  }
  return hostOnly;
};

export const ensureClarity = () => {
  if (initialized || typeof window === 'undefined') return initialized;

  try {
    Clarity.init(CLARITY_PROJECT_ID);
    Clarity.consent(true);
    initialized = true;
    return true;
  } catch {
    return false;
  }
};

export const getClaritySessionId = () => {
  if (typeof window === 'undefined') return '';

  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;

    const randomId =
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const sessionId = `shopify-app-${randomId}`;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    return sessionId;
  } catch {
    return `shopify-app-${Date.now()}`;
  }
};

export const setClarityTags = (tags: ClarityTags) => {
  if (!ensureClarity()) return;

  Object.entries(tags).forEach(([key, value]) => {
    const sanitized = sanitizeTagValue(value);
    if (!sanitized) return;

    try {
      Clarity.setTag(key, sanitized);
    } catch {
      // Clarity must never affect the app UX.
    }
  });
};

export const identifyClarityUser = ({
  shopDomain,
  sessionId,
  pageId,
  friendlyName,
}: {
  shopDomain: string;
  sessionId: string;
  pageId: string;
  friendlyName?: string;
}) => {
  if (!shopDomain || !ensureClarity()) return;

  try {
    Clarity.identify(shopDomain, sessionId || undefined, pageId || undefined, friendlyName || undefined);
  } catch {
    // Ignore analytics failures.
  }
};

export const trackClarityEvent = (eventName: string, tags: ClarityTags = {}) => {
  if (!eventName || !ensureClarity()) return;

  setClarityTags(tags);

  try {
    Clarity.event(eventName);
  } catch {
    // Ignore analytics failures.
  }
};
